import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { setAuth } from '../Redux/authSlice';
import { useDispatch } from 'react-redux';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

interface Class {
  id: number;
  name: string;
}

const Classes: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [newClassName, setNewClassName] = useState('');
  const [editableClass, setEditableClass] = useState<Class | null>(null);
  const [classToDelete, setClassToDelete] = useState<Class | null>(null);

  const dispatch = useDispatch();

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const res = await axios.get('/users/user');
        setUserRole(res.data.user.role);
        dispatch(setAuth(true));
      } catch {
        dispatch(setAuth(false));
      }
    };
    fetchUserData();
  }, [dispatch]);

  useEffect(() => {
    const fetchClassesData = async () => {
      try {
        const response = await axios.get('/Branches/classes/');
        setClasses(response.data);
      } catch {
        setError('Error fetching class data');
      } finally {
        setLoading(false);
      }
    };
    fetchClassesData();
  }, []);

  const handleCreate = async () => {
    try {
      const response = await axios.post(
        '/Branches/classes/',
        { name: newClassName },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setClasses([...classes, response.data]);
      setNewClassName('');
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating class:', error);
    }
  };

  const handleUpdate = async () => {
    if (!editableClass) return;
    try {
      const response = await axios.patch(
        `/Branches/classes/${editableClass.id}/`,
        editableClass,
        { headers: { 'Content-Type': 'application/json' } }
      );
      setClasses(classes.map(c => (c.id === editableClass.id ? response.data : c)));
      setEditableClass(null);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating class:', error);
    }
  };

  const confirmDelete = async () => {
    if (!classToDelete) return;
    try {
      await axios.delete(`/Branches/classes/${classToDelete.id}/`);
      setClasses(classes.filter(c => c.id !== classToDelete.id));
      setClassToDelete(null);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting class:', error);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-content d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <div className="alert alert-danger text-center">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-primary mb-1">Class Management</h2>
          <p className="text-muted mb-0">Manage all student classes.</p>
        </div>
        {userRole !== 'teacher' && (
          <button
            className="btn btn-primary d-flex align-items-center shadow hover-scale rounded-pill px-4"
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus className="me-2" /> Add Class
          </button>
        )}
      </div>

      <div className="card-modern shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4 py-4 text-secondary text-uppercase small fw-bold">ID</th>
                  <th className="py-4 text-secondary text-uppercase small fw-bold">Class Name</th>
                  <th className="pe-4 py-4 text-end text-secondary text-uppercase small fw-bold" style={{ width: '20%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {classes.length > 0 ? (
                  classes.map(classItem => (
                    <tr key={classItem.id} style={{ height: '60px' }}>
                      <td className="ps-4 fw-medium text-muted">#{classItem.id}</td>
                      <td className="fw-bold text-dark fs-5">{classItem.name}</td>
                      <td className="pe-4 text-end">
                        {userRole !== 'teacher' && (
                          <>
                            <button
                              className="btn btn-light btn-sm text-primary me-2 rounded-circle p-2"
                              onClick={() => {
                                setEditableClass(classItem);
                                setShowEditModal(true);
                              }}
                              title="Edit"
                            >
                              <FaEdit size={16} />
                            </button>
                            <button
                              className="btn btn-light btn-sm text-danger rounded-circle p-2"
                              onClick={() => {
                                setClassToDelete(classItem);
                                setShowDeleteModal(true);
                              }}
                              title="Delete"
                            >
                              <FaTrash size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center py-5 text-muted">
                      <i className="bi bi-inbox fs-1 d-block mb-3 opacity-50"></i>
                      No classes found. Add one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)", backdropFilter: 'blur(2px)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold text-primary">Add New Class</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body pt-4">
                <label className="form-label fw-bold text-secondary">Class Name</label>
                <input
                  type="text"
                  className="form-control form-control-lg bg-light border-0"
                  placeholder="e.g. Form One"
                  value={newClassName}
                  onChange={(e) => setNewClassName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="modal-footer border-top-0">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button className="btn btn-primary rounded-pill px-4" onClick={handleCreate} disabled={!newClassName}>Create Class</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editableClass && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)", backdropFilter: 'blur(2px)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold text-primary">Edit Class</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body pt-4">
                <label className="form-label fw-bold text-secondary">Class Name</label>
                <input
                  type="text"
                  className="form-control form-control-lg bg-light border-0"
                  value={editableClass.name}
                  onChange={(e) => setEditableClass({ ...editableClass, name: e.target.value })}
                  autoFocus
                />
              </div>
              <div className="modal-footer border-top-0">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="btn btn-primary rounded-pill px-4" onClick={handleUpdate}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)", backdropFilter: 'blur(2px)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-body p-4 text-center">
                <div className="bg-danger bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                  <FaTrash className="text-danger fs-3" />
                </div>
                <h4 className="fw-bold text-dark">Delete Class?</h4>
                <p className="text-muted mb-4">
                  Are you sure you want to delete <strong>{classToDelete?.name}</strong>?<br />
                  This action cannot be undone.
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                  <button className="btn btn-danger rounded-pill px-4" onClick={confirmDelete}>Yes, Delete</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Classes;
